/**
 * AMENDED 2026-10-04: one arm by default; B exists only when the owner supplies a second questionnaire.
 * Earlier A/B publications remain valid. The original surveybot provenance below describes that
 * earlier shape; equal arm lengths are no longer required when each arm supplies its ordinals.
 *
 * `surveybot-survey-json-v1` — the survey template's **reference schema** (unit P7-01).
 *
 * Read out of `surveybot/server/script.ts` and `surveybot/survey/*.json`, not guessed. The
 * real shape is: **one JSON file per language**, validated at startup against a zod schema
 * that throws with `path: message` for every issue, loaded into
 * `SURVEYS = new Map<Locale, Survey>()` with a **duplicate-language throw**, and holding
 * *every approved line the bot may say* — "the server never improvises text: everything
 * spoken comes from here".
 *
 * That last sentence is the whole reason this template is not a list of questions. A survey
 * agent is a **script**, and the script includes the greeting by time of day, the scale
 * explanation with the statement count spelled out as a word, the ordinal before each
 * statement, a fixed answer to each of eight anticipated objections, a de-escalation line, six
 * helper lines for silence and confusion, and the acknowledgements. All of it is approved copy
 * that a research client signed off; none of it is something a model may paraphrase.
 *
 * WHAT THIS CHANGES FROM SURVEYBOT, AND WHY
 *
 * 1. **Language is a `LanguageCode`, not a two-value enum.** surveybot closes `Locale` over
 *    `["el","he"]`. Design 9.4 requires language 27 to be "a JSON file plus a voice id, with
 *    no TypeScript change", so the code is validated BCP-47 and availability is data.
 * 2. **The statement count is a coupling, not the literal 6.** surveybot writes
 *    `.length(6)` on both variants. What that constant is really standing in for is visible
 *    two fields away: `scale.ordinals` is indexed per statement and `count_words[n]` spells
 *    the count aloud. So the rule here is "both variants agree on N, there is an ordinal for
 *    every statement, and a count word for N" — strictly stronger where it matters and not
 *    frozen at six. (Re-hosting surveybot unchanged therefore needs its `.length(6)` relaxed
 *    to the same coupling; that is one line in the bot and is recorded in the unit report.)
 * 3. **Failure is a located value, not a throw.** surveybot throws at startup, which is right
 *    for a single-shop process. A build job has to *record* the failure and the Building
 *    screen has to render it, so every entry point here returns `{ ok: false, problem,
 *    location }` with the JSON path in `location.key` (AC-13).
 * 4. **No reference-locale fallback.** surveybot's `DEFAULT_LOCALE` is the first file listed
 *    and browser sessions fall back to it. Here a language with no questionnaire is simply
 *    not available (design 9.4, INV-9).
 *
 * WHAT IT KEEPS, VERBATIM: every field name, every required-ness, `{greeting}` inside
 * `opening.intro`, `{count}` inside `scale.intro`, the A/B variant split with its per-variant
 * `emotion` and `source` switches, legacy emotion/source ids with their keyword lists (new choice questions
 * declare their own options and selection limits), all eight objection types with `hostile` alone allowed an
 * empty answer, the six helper lines, and unique statement ids **across** variants.
 *
 * AMENDED 2026-10-11 (ADR 0053, the product owner's decision): **a questionnaire is an ordered list of typed
 * questions.** A survey asks exactly the questions its document holds — any number, in the document's order —
 * each one of four types: `scale` (yes / no / maybe, or the document's own scale words), `choice` (one, or up to
 * N, of the document's options), `open` (the respondent's own words) and `number` (a count, a rating, an age).
 * There are no fixed tail questions any more. New builds write `wetalk-survey-json-v2` files
 * ({@link SurveyFileV2Schema}); every published `surveybot-survey-json-v1` file keeps working unchanged: the
 * reader maps it onto the same model ({@link SurveyDocument}) — its statements become scale questions and its
 * emotion / source questions become choice questions at the end, only where their arm's switch is on, spoken
 * exactly as before (unnumbered, filed under their old kinds). Nothing stored is rewritten.
 */
import { z } from "zod";
import { isLanguageCode } from "../language-code.js";
/** What a respondent's answer to a scale question is recorded as. surveybot's `ANSWERS`. */
export const SURVEY_ANSWER = ["yes", "no", "strongly_agree", "strongly_disagree", "maybe", "dont_know", "no_answer", "unclear"];
/** The answers that are not an answer — any question type may be given one of these. */
export const SURVEY_NON_ANSWER = ["dont_know", "no_answer", "unclear"];
/** What an open question's `survey_answer.answer` reads when the respondent's own words were kept. */
export const SURVEY_OPEN_ANSWERED = "answered";
/** The closed emotion vocabulary of v1's tail question (kept for published v1 questionnaires). */
export const SURVEY_EMOTION = [
    "anger",
    "fear",
    "disappointment",
    "indifference",
    "hope",
    "satisfaction",
];
/** Where the respondent gets their news — v1's tail question (kept for published v1 questionnaires). */
export const SURVEY_SOURCE = ["radio", "tv", "internet", "social", "other", "none"];
/**
 * The eight anticipated objections.
 *
 * `hostile` is mockery or an insult: it is met with silence and the question again, which is
 * why it is the one type allowed an empty `answer`.
 */
export const SURVEY_OBJECTION = [
    "what_is_this",
    "are_you_local",
    "who_runs",
    "where_phone",
    "are_you_robot",
    "how_long",
    "unknown",
    "hostile",
];
/** How a survey conversation ended. surveybot's `OUTCOMES`, which the template's vocabulary uses. */
export const SURVEY_OUTCOME = [
    "completed",
    "declined_busy",
    "declined",
    "stopped",
    "closed_after_objections",
    "no_response",
    "hung_up",
    "disconnected",
];
export const SURVEY_VARIANT = ["A", "B"];
/** ADR 0053: the four question types a questionnaire may hold, in any number and order. */
export const SURVEY_QUESTION_TYPE = ["scale", "choice", "open", "number"];
/**
 * What `survey_answer.question_kind` files an answer under. `statement`, `emotion` and `source` are v1's (a v2
 * scale question files as `statement` too, so one results path reads both); `choice`, `open` and `number` are
 * v2's other three types.
 */
export const SURVEY_QUESTION_KIND = ["statement", "emotion", "source", "choice", "open", "number"];
/** The extraction tags: a published v1 questionnaire, and the typed-question files every new build writes. */
export const SURVEY_FILE_V1 = "surveybot-survey-json-v1";
export const SURVEY_FILE_V2 = "wetalk-survey-json-v2";
const NonEmpty = z.string().min(1);
/**
 * A question id: one to sixteen capital letters (any script, `\p{Lu}`) and digits — the questionnaire's
 * own code. Exported so a consumer that needs to recognise one uses the same rule.
 */
export const STATEMENT_ID_SHAPE = /^[\p{Lu}\p{Nd}]{1,16}$/u;
/** Choice ids are instrument data, kept within survey_answer.answer's storage bound. */
const OptionSchema = z.object({
    id: z.string().regex(/^[a-z][a-z0-9_]{0,31}$/u).refine((id) => !SURVEY_NON_ANSWER.includes(id) && id !== SURVEY_OPEN_ANSWERED, "reserved non-answer id"),
    label: NonEmpty,
    keywords: z.array(NonEmpty).min(1),
}).strict();
const SelectionSchema = z.object({
    min: z.number().int().min(1),
    max: z.number().int().min(1),
    /** Options such as 'none' that cannot be combined with another selection. */
    exclusive: z.array(NonEmpty),
    /** Approved follow-up after a partial selection; asks for another OR confirms these are all. */
    more: NonEmpty.optional(),
}).strict();
function refineChoice(question, context) {
    const issue = (path, message) => context.addIssue({ code: "custom", path, message });
    const ids = question.options.map((option) => option.id);
    if (new Set(ids).size !== ids.length)
        issue(["options"], "Option ids must be unique within a question.");
    const selection = question.selection;
    if (selection === undefined)
        return;
    if (selection.min > selection.max || selection.max > ids.length)
        issue(["selection"], "Selection limits must satisfy 1 <= min <= max <= option count.");
    if (new Set(selection.exclusive).size !== selection.exclusive.length || selection.exclusive.some((id) => !ids.includes(id))) {
        issue(["selection", "exclusive"], "Exclusive choices must be distinct configured option ids.");
    }
    if (selection.max > 1 && selection.more === undefined)
        issue(["selection", "more"], "Multiple-choice questions need an approved selection.more follow-up.");
}
const ChoiceQuestionSchema = z.object({
    id: NonEmpty,
    code: NonEmpty,
    text: NonEmpty,
    clarify: NonEmpty,
    options: z.array(OptionSchema).min(1),
    /** Absent only on older publications, whose original choice handling stays compatible. */
    selection: SelectionSchema.optional(),
}).strict().superRefine(refineChoice);
const StatementSchema = z
    .object({
    /**
     * Unique across BOTH variants. AMENDED 2026-10-02 (product owner): the questionnaire's own
     * code, in its own alphabet — `Α1`, `ΣΤ2`, `Z2` — so capital letters of any script and digits,
     * not ascii only. surveybot's ascii rule refused a Greek instrument's codes outright, and
     * nothing downstream needs ascii: the id is a key and a prompt label, never a path or a column.
     */
    id: z
        .string()
        .regex(STATEMENT_ID_SHAPE, "must be the questionnaire's own code: capital letters and digits, like Α1 or ΣΤ2"),
    /** The research instrument's own code, in the instrument's own alphabet: `Ζ2`, `Α1`. */
    code: NonEmpty,
    text: z.string().min(5),
    note: z.string().optional(),
})
    .strict();
const VariantSchema = z
    .object({
    statements: z.array(StatementSchema).min(1),
    emotion: z.boolean(),
    source: z.boolean(),
    /** Optional per-arm ordinals when the arms contain different numbers of statements. */
    ordinals: z.array(NonEmpty).min(1).optional(),
})
    .strict();
/** The fields every questionnaire carries besides its questions — the approved script around them. */
const scriptShape = {
    title: NonEmpty,
    /** The interviewer's spoken name. It appears inside `opening.intro` as approved copy. */
    interviewer: NonEmpty,
    language: z.string().refine(isLanguageCode, "not a BCP-47 language code"),
    /** Spoken greeting by time of day, substituted into `opening.intro`. */
    greetings: z.object({ morning: NonEmpty, afternoon: NonEmpty }).strict(),
    /** Numbers as words, index 1 upwards ("μία", "δύο", …), for `{count}` in the scale intro. */
    count_words: z.array(z.string()).min(2),
    /** Bias terms beyond the option labels: "ναι", "ίσως", "δεν ξέρω", "ρομπότ". */
    recognition_terms: z.array(NonEmpty).min(1),
    opening: z
        .object({
        intro: z.string().includes("{greeting}"),
        declined: NonEmpty,
    })
        .strict(),
    closing: NonEmpty,
    objections: z.record(z.enum(SURVEY_OBJECTION), z.object({ asks: z.string(), answer: z.string() }).strict()),
    deescalation: NonEmpty,
    helpers: z
        .object({
        unclear: NonEmpty,
        /** They said something, but not a position on the statement. */
        clarify_scale: NonEmpty,
        presence: NonEmpty,
        presence2: NonEmpty,
        wait: NonEmpty,
        no_response_close: NonEmpty,
    })
        .strict(),
    acks: z.array(NonEmpty).min(1),
};
/**
 * The v1 questionnaire file, field for field as surveybot's `SurveySchema` declares it.
 *
 * Key names stay snake_case because this document **is** the file a survey agent consumes;
 * the camelCase convention of the runtime configuration governs the fields
 * `RuntimeConfigSchema` itself names, and `extraction` is template-shaped by design.
 */
export const SurveyDocumentSchema = z
    .object({
    ...scriptShape,
    scale: z
        .object({
        intro: z.string().includes("{count}"),
        suffix: NonEmpty,
        ordinals: z.array(NonEmpty).min(1),
    })
        .strict(),
    variants: z.object({ A: VariantSchema, B: VariantSchema.optional() }).strict(),
    emotion: ChoiceQuestionSchema,
    source: ChoiceQuestionSchema,
})
    .strict();
// ---- v2: an ordered list of typed questions (ADR 0053) ------------------------------------------
const QuestionIdSchema = z
    .string()
    .regex(STATEMENT_ID_SHAPE, "must be the questionnaire's own code: capital letters and digits, like Α1 or Q7");
const ScaleQuestionV2Schema = z.object({
    id: QuestionIdSchema,
    code: NonEmpty,
    type: z.literal("scale"),
    text: z.string().min(5),
    note: z.string().optional(),
    /** This question's own scale words, when they differ from `scale.suffix` ("Agree, disagree, or neither?"). */
    suffix: NonEmpty.optional(),
}).strict();
const ChoiceQuestionV2Schema = z.object({
    id: QuestionIdSchema,
    code: NonEmpty,
    type: z.literal("choice"),
    text: NonEmpty,
    clarify: NonEmpty,
    options: z.array(OptionSchema).min(1),
    selection: SelectionSchema,
}).strict();
const OpenQuestionV2Schema = z.object({
    id: QuestionIdSchema,
    code: NonEmpty,
    type: z.literal("open"),
    text: NonEmpty,
    clarify: NonEmpty,
}).strict();
const NumberQuestionV2Schema = z.object({
    id: QuestionIdSchema,
    code: NonEmpty,
    type: z.literal("number"),
    text: NonEmpty,
    /** Asked when the reply was not a number, or a number outside the range. */
    clarify: NonEmpty,
    min: z.number().finite().optional(),
    max: z.number().finite().optional(),
    /** Whole numbers only (a count, an age); false lets 7.5 stand. */
    integer: z.boolean(),
    /** The unit the number is in, as the document words it ("years", "people"). */
    unit: NonEmpty.optional(),
}).strict();
const QuestionV2Schema = z.discriminatedUnion("type", [
    ScaleQuestionV2Schema,
    ChoiceQuestionV2Schema,
    OpenQuestionV2Schema,
    NumberQuestionV2Schema,
]).superRefine((question, context) => {
    if (question.type === "choice")
        refineChoice(question, context);
    if (question.type === "number" && question.min !== undefined && question.max !== undefined && question.min > question.max) {
        context.addIssue({ code: "custom", path: ["min"], message: "A number question's min must not exceed its max." });
    }
});
const VariantV2Schema = z
    .object({
    questions: z.array(QuestionV2Schema).min(1),
    /** Per-arm ordinals when the arms hold different numbers of questions. */
    ordinals: z.array(NonEmpty).min(1).optional(),
})
    .strict();
/** A v2 questionnaire file (`wetalk-survey-json-v2`): the approved script around an ordered list of typed questions. */
export const SurveyFileV2Schema = z
    .object({
    ...scriptShape,
    /**
     * Spoken greeting by time of day, substituted into `opening.intro`. AMENDED 2026-10-21 (plan-026, design
     * §37.4.4): an optional `evening` for a language that greets in three slots (buenos días / buenas tardes /
     * buenas noches), said from the pack's night hour (`greeting.slot_hour`). Additive: without it the two-slot
     * rule is today's, and the file stays `wetalk-survey-json-v2`.
     */
    greetings: z.object({ morning: NonEmpty, afternoon: NonEmpty, evening: NonEmpty.optional() }).strict(),
    scale: z
        .object({
        /** Spoken once before the first question; `{count}`, when present, is the number of questions as a word. */
        intro: NonEmpty,
        /** The scale questions' words ("Yes, no, or maybe?"), unless a question names its own. */
        suffix: NonEmpty,
        /** One per question, the last announcing the last ("First question.", …, "Last question."). */
        ordinals: z.array(NonEmpty).min(1),
    })
        .strict(),
    variants: z.object({ A: VariantV2Schema, B: VariantV2Schema.optional() }).strict(),
})
    .strict();
/** Only arms actually supplied by the owner, in their presentation order. */
export function surveyVariantsOf(survey) {
    return survey.variants.B === undefined ? ["A"] : ["A", "B"];
}
/** Refuse an unavailable arm instead of silently assigning another questionnaire. */
export function surveyArmOf(survey, variant) {
    const arm = survey.variants[variant];
    if (arm === undefined)
        throw new Error(`Survey has no arm ${variant}.`);
    return arm;
}
/** The arm's question at `index`, or `null`. */
export function surveyQuestionAt(survey, variant, index) {
    return surveyArmOf(survey, variant).questions[index] ?? null;
}
/** Every question of every arm, each id once (a question asked in both arms is listed once). */
export function surveyQuestionListOf(survey) {
    const seen = new Set();
    const out = [];
    for (const variant of surveyVariantsOf(survey)) {
        for (const question of surveyArmOf(survey, variant).questions) {
            if (seen.has(question.id))
                continue;
            seen.add(question.id);
            out.push(question);
        }
    }
    return out;
}
/** How many questions of the arm are numbered — what `{count}` and the ordinals count. */
export function numberedCountOf(arm) {
    return arm.questions.filter((question) => question.numbered).length;
}
/** The values an answer to `question` may be filed as; `number` for a number question's numbers. */
export function surveyAnswerValuesOf(question) {
    switch (question.type) {
        case "scale":
            return SURVEY_ANSWER;
        case "choice": {
            const vocabulary = question.legacy === null || question.selection !== undefined
                ? question.options.map((option) => option.id)
                : question.legacy === "emotion" ? SURVEY_EMOTION : SURVEY_SOURCE;
            return [...new Set([...vocabulary, ...SURVEY_NON_ANSWER])];
        }
        case "open":
            return [SURVEY_OPEN_ANSWERED, ...SURVEY_NON_ANSWER];
        case "number":
            return "number";
    }
}
/** A recorded number as `survey_answer.answer` spells it: plain decimal digits, never an exponent. */
export function surveyNumberText(value) {
    if (!Number.isFinite(value))
        throw new RangeError("A survey number must be finite.");
    const text = Number.isInteger(value) ? value.toFixed(0) : String(Math.round(value * 1_000_000) / 1_000_000);
    if (/e/iu.test(text) || text.length > 32)
        throw new RangeError("A survey number is too large to record.");
    return text;
}
/** The shape of a recorded number's text. */
export const SURVEY_NUMBER_TEXT_SHAPE = /^-?\d{1,24}(\.\d{1,6})?$/u;
/** Whether a recorded value is valid for the question (a number question's numbers as their text). */
export function isSurveyAnswerValue(question, value) {
    const values = surveyAnswerValuesOf(question);
    if (values !== "number")
        return values.includes(value);
    if (SURVEY_NON_ANSWER.includes(value))
        return true;
    return SURVEY_NUMBER_TEXT_SHAPE.test(value);
}
function problemOf(problem, file, key) {
    return {
        ok: false,
        problem,
        location: key === undefined ? { file } : { file, key },
    };
}
function zodProblem(error, file) {
    const issue = error.issues[0];
    const path = issue === undefined ? "" : issue.path.join(".");
    const detail = error.issues
        .map((one) => `${one.path.join(".") || "(root)"}: ${one.message}`)
        .join("; ");
    return problemOf(detail, file, path === "" ? undefined : path);
}
function objectionProblem(survey, file) {
    for (const type of SURVEY_OBJECTION) {
        const answer = survey.objections[type];
        if (answer === undefined) {
            return problemOf(`The questionnaire has no answer for the "${type}" objection. All eight are ` +
                "anticipated; an unanswered one becomes an improvised one.", file, `objections.${type}`);
        }
        // `hostile` alone is met with silence and the question again, so its answer is empty.
        if (type !== "hostile" && answer.answer.trim() === "") {
            return problemOf(`The "${type}" objection has an empty answer. Only "hostile" may be silent.`, file, `objections.${type}.answer`);
        }
    }
    return null;
}
/** Owner-approved Greek copy, shared by saved scripts, new builds and speech pre-synthesis. */
function surveyAcknowledgementsOf(survey) {
    if (survey.language.split("-")[0] !== "el")
        return survey.acks;
    const allowed = survey.acks.filter((line) => !/μαλιστα/u.test(line.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase("el")));
    return [...new Set([...allowed,
            "Κατανοητό.", "Καταλαβαίνω.", "Ευχαριστώ.", "Σας ευχαριστώ.", "Το σημειώνω.", "Εντάξει.",
        ])];
}
/** v1 → the model: statements as scale questions, then emotion and source where their switch is on. */
function documentOfV1(file) {
    const choice = (kind) => {
        const question = file[kind];
        return {
            type: "choice", id: question.id, code: question.code, text: question.text, clarify: question.clarify,
            options: question.options, ...(question.selection === undefined ? {} : { selection: question.selection }),
            legacy: kind, numbered: false, kind,
        };
    };
    const armOf = (arm) => ({
        questions: [
            ...arm.statements.map((statement) => ({
                type: "scale", id: statement.id, code: statement.code, text: statement.text,
                ...(statement.note === undefined ? {} : { note: statement.note }),
                suffix: file.scale.suffix, numbered: true, kind: "statement",
            })),
            ...(arm.emotion ? [choice("emotion")] : []),
            ...(arm.source ? [choice("source")] : []),
        ],
        ordinals: arm.ordinals ?? file.scale.ordinals,
    });
    return {
        format: SURVEY_FILE_V1,
        title: file.title, interviewer: file.interviewer, language: file.language, greetings: file.greetings,
        count_words: file.count_words, recognition_terms: file.recognition_terms, opening: file.opening,
        scale: { intro: file.scale.intro, suffix: file.scale.suffix },
        variants: { A: armOf(file.variants.A), ...(file.variants.B === undefined ? {} : { B: armOf(file.variants.B) }) },
        closing: file.closing, objections: file.objections, deescalation: file.deescalation, helpers: file.helpers, acks: surveyAcknowledgementsOf(file),
        legacyChoice: { emotion: choice("emotion"), source: choice("source") },
    };
}
function choiceFileOf(question) {
    return {
        id: question.id, code: question.code, text: question.text, clarify: question.clarify,
        options: question.options.map((option) => ({ id: option.id, label: option.label, keywords: [...option.keywords] })),
        ...(question.selection === undefined ? {} : {
            selection: {
                min: question.selection.min, max: question.selection.max, exclusive: [...question.selection.exclusive],
                ...(question.selection.more === undefined ? {} : { more: question.selection.more }),
            },
        }),
    };
}
/**
 * The model written back as the file it was read from — v1 to its surveybot shape, v2 to its typed-question
 * shape. A file read and written back reads into the same model.
 */
export function surveyFileOf(survey) {
    const script = {
        title: survey.title, interviewer: survey.interviewer, language: survey.language,
        greetings: { morning: survey.greetings.morning, afternoon: survey.greetings.afternoon },
        count_words: [...survey.count_words], recognition_terms: [...survey.recognition_terms],
        opening: { intro: survey.opening.intro, declined: survey.opening.declined },
        closing: survey.closing, objections: survey.objections, deescalation: survey.deescalation, helpers: survey.helpers, acks: [...survey.acks],
    };
    const sharedOrdinals = [...survey.variants.A.ordinals];
    const ownOrdinals = (arm) => JSON.stringify(arm.ordinals) === JSON.stringify(sharedOrdinals) ? {} : { ordinals: [...arm.ordinals] };
    if (survey.format === SURVEY_FILE_V1) {
        const legacy = survey.legacyChoice;
        if (legacy === undefined)
            throw new Error("A v1 questionnaire holds its emotion and source questions.");
        const armFile = (arm) => ({
            statements: arm.questions.flatMap((question) => (question.type === "scale"
                ? [{ id: question.id, code: question.code, text: question.text, ...(question.note === undefined ? {} : { note: question.note }) }]
                : [])),
            emotion: arm.questions.some((question) => question.kind === "emotion"),
            source: arm.questions.some((question) => question.kind === "source"),
            ...ownOrdinals(arm),
        });
        const asked = [...survey.variants.A.questions, ...(survey.variants.B?.questions ?? [])];
        const emotion = asked.find((question) => question.kind === "emotion") ?? legacy.emotion;
        const source = asked.find((question) => question.kind === "source") ?? legacy.source;
        return {
            ...script,
            scale: { intro: survey.scale.intro, suffix: survey.scale.suffix, ordinals: sharedOrdinals },
            variants: { A: armFile(survey.variants.A), ...(survey.variants.B === undefined ? {} : { B: armFile(survey.variants.B) }) },
            emotion: choiceFileOf(emotion.type === "choice" ? emotion : legacy.emotion),
            source: choiceFileOf(source.type === "choice" ? source : legacy.source),
        };
    }
    const questionFile = (question) => {
        const base = { id: question.id, code: question.code, text: question.text };
        switch (question.type) {
            case "scale":
                return {
                    ...base, type: "scale", ...(question.note === undefined ? {} : { note: question.note }),
                    ...(question.suffix === survey.scale.suffix ? {} : { suffix: question.suffix }),
                };
            case "choice": {
                const selection = choiceFileOf(question).selection;
                if (selection === undefined)
                    throw new Error(`Choice question ${question.id} has no selection limits.`);
                return { ...base, type: "choice", clarify: question.clarify, options: choiceFileOf(question).options, selection };
            }
            case "open":
                return { ...base, type: "open", clarify: question.clarify };
            case "number":
                return {
                    ...base, type: "number", clarify: question.clarify, integer: question.integer,
                    ...(question.min === null ? {} : { min: question.min }), ...(question.max === null ? {} : { max: question.max }),
                    ...(question.unit === null ? {} : { unit: question.unit }),
                };
        }
    };
    const armFile = (arm) => ({ questions: arm.questions.map(questionFile), ...ownOrdinals(arm) });
    return {
        ...script,
        greetings: { ...script.greetings, ...(survey.greetings.evening === undefined ? {} : { evening: survey.greetings.evening }) },
        scale: { intro: survey.scale.intro, suffix: survey.scale.suffix, ordinals: sharedOrdinals },
        variants: { A: armFile(survey.variants.A), ...(survey.variants.B === undefined ? {} : { B: armFile(survey.variants.B) }) },
    };
}
/**
 * ADDED 2026-10-28 (plan-030 DOM-Q, design §41.6.6, OD-6): a published v1 file moved to the current format, for the
 * survey question editor. Its statements become scale questions (ids, codes and notes kept; they speak the file's
 * `scale.suffix`, as before), and its emotion and source questions — only in the arms that asked them — become
 * choice questions at the end of that arm with their ids, codes, wording, option ids, labels and keywords kept, and
 * their own selection limits, or one pick (`{ min: 1, max: 1, exclusive: [] }`) where the v1 file had none. Every code
 * and option id is unchanged, so results stay together.
 *
 * One thing changes in how it is spoken, which the editor tells the owner (`format_converted`): the two choice
 * questions are now numbered like the others, so the arm needs that many more ordinals and, with `{count}` in the
 * explanation, a count word for the new number. This function adds no words: the ordinals and count words stay the
 * v1 file's, and `surveyQuestionnaireEditOf` adds the missing ones from the language pack, marked `wording_added`
 * (or names them `wording_missing` in a language without the pack's phrases). Pure.
 */
export function surveyFileV2Of(file) {
    const choiceOf = (kind) => {
        const question = file[kind];
        const selection = question.selection;
        return {
            id: question.id, code: question.code, type: "choice", text: question.text, clarify: question.clarify,
            options: question.options.map((option) => ({ id: option.id, label: option.label, keywords: [...option.keywords] })),
            selection: selection === undefined
                ? { min: 1, max: 1, exclusive: [] }
                : {
                    min: selection.min, max: selection.max, exclusive: [...selection.exclusive],
                    ...(selection.more === undefined ? {} : { more: selection.more }),
                },
        };
    };
    const armOf = (arm) => ({
        questions: [
            ...arm.statements.map((statement) => ({
                id: statement.id, code: statement.code, type: "scale", text: statement.text,
                ...(statement.note === undefined ? {} : { note: statement.note }),
            })),
            ...(arm.emotion ? [choiceOf("emotion")] : []),
            ...(arm.source ? [choiceOf("source")] : []),
        ],
        ...(arm.ordinals === undefined ? {} : { ordinals: [...arm.ordinals] }),
    });
    return {
        title: file.title, interviewer: file.interviewer, language: file.language,
        greetings: { morning: file.greetings.morning, afternoon: file.greetings.afternoon },
        count_words: [...file.count_words], recognition_terms: [...file.recognition_terms],
        opening: { intro: file.opening.intro, declined: file.opening.declined },
        closing: file.closing,
        objections: Object.fromEntries(Object.entries(file.objections).map(([type, one]) => [type, { asks: one.asks, answer: one.answer }])),
        deescalation: file.deescalation, helpers: { ...file.helpers }, acks: [...file.acks],
        scale: { intro: file.scale.intro, suffix: file.scale.suffix, ordinals: [...file.scale.ordinals] },
        variants: { A: armOf(file.variants.A), ...(file.variants.B === undefined ? {} : { B: armOf(file.variants.B) }) },
    };
}
/** A stored extraction for one or more models (all of one format): what a build stores, as a test writes it. */
export function surveyExtractionFileOf(survey) {
    const first = survey[0];
    if (first === undefined)
        throw new Error("An extraction holds at least one questionnaire.");
    return { referenceSchema: first.format, survey: Object.fromEntries(survey.map((one) => [one.language, surveyFileOf(one)])) };
}
function questionOfV2(question, file) {
    const base = { id: question.id, code: question.code, text: question.text, numbered: true };
    switch (question.type) {
        case "scale":
            return { ...base, type: "scale", kind: "statement", suffix: question.suffix ?? file.scale.suffix, ...(question.note === undefined ? {} : { note: question.note }) };
        case "choice":
            return { ...base, type: "choice", kind: "choice", clarify: question.clarify, options: question.options, selection: question.selection, legacy: null };
        case "open":
            return { ...base, type: "open", kind: "open", clarify: question.clarify };
        case "number":
            return {
                ...base, type: "number", kind: "number", clarify: question.clarify,
                min: question.min ?? null, max: question.max ?? null, integer: question.integer, unit: question.unit ?? null,
            };
    }
}
function documentOfV2(file) {
    const armOf = (arm) => ({
        questions: arm.questions.map((question) => questionOfV2(question, file)),
        ordinals: arm.ordinals ?? file.scale.ordinals,
    });
    return {
        format: SURVEY_FILE_V2,
        title: file.title, interviewer: file.interviewer, language: file.language, greetings: file.greetings,
        count_words: file.count_words, recognition_terms: file.recognition_terms, opening: file.opening,
        scale: { intro: file.scale.intro, suffix: file.scale.suffix },
        variants: { A: armOf(file.variants.A), ...(file.variants.B === undefined ? {} : { B: armOf(file.variants.B) }) },
        closing: file.closing, objections: file.objections, deescalation: file.deescalation, helpers: file.helpers, acks: surveyAcknowledgementsOf(file),
    };
}
/** Whether a stored or uploaded value is shaped as a v2 file (its arms hold `questions`, not `statements`). */
export function isSurveyFileV2Shaped(value) {
    if (typeof value !== "object" || value === null)
        return false;
    const variants = value.variants;
    if (typeof variants !== "object" || variants === null)
        return false;
    const arm = variants.A;
    return typeof arm === "object" && arm !== null && "questions" in arm;
}
/** The arm-level checks both formats share: one ordinal per numbered question, and a count word when spoken. */
function armProblem(survey, file) {
    const v1 = survey.format === SURVEY_FILE_V1;
    for (const variant of surveyVariantsOf(survey)) {
        const arm = surveyArmOf(survey, variant);
        const count = numberedCountOf(arm);
        if (arm.ordinals.length !== count) {
            return problemOf(v1
                ? `Arm ${variant} has ${String(count)} statements and needs exactly that many ordinals. ` +
                    "When arms have different lengths, supply ordinals in each arm so its last statement is announced correctly."
                : `Arm ${variant} has ${String(count)} questions and needs exactly that many ordinals. ` +
                    "When arms have different lengths, supply ordinals in each arm so its last question is announced correctly.", file, `variants.${variant}.ordinals`);
        }
        if (survey.scale.intro.includes("{count}") && (survey.count_words[count] ?? "").trim() === "") {
            return problemOf(`count_words needs a non-empty entry at index ${String(count)} to announce arm ${variant}'s ${v1 ? "statement" : "question"} count.`, file, "count_words");
        }
    }
    return null;
}
/** A question asked in both arms (or in every language) must be the same question: same type, options and limits. */
function questionContractOf(question) {
    switch (question.type) {
        case "choice":
            return JSON.stringify({
                type: question.type, options: question.options.map((option) => option.id),
                min: question.selection?.min, max: question.selection?.max, exclusive: question.selection?.exclusive,
            });
        case "number":
            return JSON.stringify({ type: question.type, min: question.min, max: question.max, integer: question.integer });
        default:
            return JSON.stringify({ type: question.type });
    }
}
/**
 * Validates one questionnaire file of either format and reads it into the model.
 *
 * `location.key` carries the JSON path of the first issue (`variants.A.questions.3.text`),
 * which is what AC-13 means by "names the problem and its location" for a JSON upload: there
 * is no useful line number in a re-serialised document, but there is always a path.
 */
export function surveyDocumentOf(value, file) {
    if (isSurveyFileV2Shaped(value))
        return surveyDocumentOfV2(value, file);
    const parsed = SurveyDocumentSchema.safeParse(value);
    if (!parsed.success)
        return zodProblem(parsed.error, file);
    const raw = parsed.data;
    const statementId = [
        ...raw.variants.A.statements,
        ...(raw.variants.B?.statements ?? []),
    ].map((one) => one.id);
    const seen = new Set();
    for (const one of statementId) {
        if (seen.has(one)) {
            return problemOf(`Statement id "${one}" appears twice. Ids must be unique across BOTH variants, ` +
                "because one recorded answer set holds them together.", file, "variants");
        }
        seen.add(one);
    }
    for (const kind of ["emotion", "source"]) {
        const question = raw[kind];
        if (seen.has(question.id))
            return problemOf("Question ids must be unique.", file, `${kind}.id`);
        seen.add(question.id);
        const ids = question.options.map((option) => option.id);
        if (new Set(ids).size !== ids.length)
            return problemOf("Option ids must be unique within a question.", file, `${kind}.options`);
        const selection = question.selection;
        if (selection === undefined) {
            const legacy = kind === "emotion" ? SURVEY_EMOTION : SURVEY_SOURCE;
            if (ids.some((id) => !legacy.includes(id)))
                return problemOf("New choice questions must specify selection limits.", file, `${kind}.selection`);
        }
    }
    const survey = documentOfV1(raw);
    const arm = armProblem(survey, file);
    if (arm !== null)
        return arm;
    const objection = objectionProblem(raw, file);
    if (objection !== null)
        return objection;
    return { ok: true, survey, file: raw };
}
function surveyDocumentOfV2(value, file) {
    const parsed = SurveyFileV2Schema.safeParse(value);
    if (!parsed.success)
        return zodProblem(parsed.error, file);
    const raw = parsed.data;
    const survey = documentOfV2(raw);
    const byId = new Map();
    for (const variant of surveyVariantsOf(survey)) {
        const inArm = new Set();
        for (const [index, question] of surveyArmOf(survey, variant).questions.entries()) {
            if (inArm.has(question.id)) {
                return problemOf(`Question id "${question.id}" appears twice in arm ${variant}. Ids must be unique within an arm.`, file, `variants.${variant}.questions.${String(index)}.id`);
            }
            inArm.add(question.id);
            const held = byId.get(question.id);
            if (held !== undefined && questionContractOf(held) !== questionContractOf(question)) {
                return problemOf(`Question id "${question.id}" is a different question in each arm. A question asked in both arms must be the ` +
                    "same question; give different questions different ids.", file, `variants.${variant}.questions.${String(index)}.id`);
            }
            byId.set(question.id, question);
        }
    }
    const arm = armProblem(survey, file);
    if (arm !== null)
        return arm;
    const objection = objectionProblem(raw, file);
    if (objection !== null)
        return objection;
    return { ok: true, survey, file: raw };
}
/** Parses a questionnaire file's text. Malformed JSON is located at the file, not a path. */
export function surveyDocumentOfJson(text, file) {
    let value;
    try {
        value = JSON.parse(text);
    }
    catch (raised) {
        return problemOf(`The questionnaire is not valid JSON: ${raised instanceof Error ? raised.message : String(raised)}`, file, undefined);
    }
    return surveyDocumentOf(value, file);
}
/** Every language must ask the same questions: same ids, types and order, same options, limits and ranges. */
function languageContractOf(survey) {
    return JSON.stringify(surveyVariantsOf(survey).map((variant) => surveyArmOf(survey, variant).questions
        .filter((question) => survey.format === SURVEY_FILE_V2 || (question.type === "choice" && question.selection !== undefined))
        .map((question) => ({ id: question.id, contract: questionContractOf(question) }))));
}
/** Validates every uploaded questionnaire and keys them by their own declared language. */
export function surveyQuestionnaireOf(upload, rule = "any") {
    if (upload.length === 0) {
        return problemOf("A survey VoiceAgent needs at least one questionnaire file; every line it speaks comes " +
            "from one.", "(no file)", undefined);
    }
    const survey = {};
    const raw = {};
    const claimedBy = new Map();
    let armCount;
    let format;
    let contract;
    for (const one of upload) {
        const parsed = surveyDocumentOfJson(one.text, one.file);
        if (!parsed.ok)
            return parsed;
        if (rule === "v2" && parsed.survey.format !== SURVEY_FILE_V2) {
            return problemOf("Write the questionnaire as an ordered list of typed questions: each arm holds \"questions\", each with a " +
                "\"type\" (scale, choice, open or number). There are no separate statements, emotion or source fields.", one.file, "variants");
        }
        if (format !== undefined && parsed.survey.format !== format) {
            return problemOf("Every language's questionnaire must be written in the same format.", one.file, "variants");
        }
        format = parsed.survey.format;
        const count = surveyVariantsOf(parsed.survey).length;
        if (armCount !== undefined && count !== armCount) {
            return problemOf("Every language must contain the same number of survey arms.", one.file, "variants");
        }
        armCount = count;
        const language = parsed.survey.language;
        const already = claimedBy.get(language);
        if (already !== undefined) {
            return problemOf(`Two questionnaire files claim language "${language}": ${already} and ${one.file}.`, one.file, "language");
        }
        const contractHere = languageContractOf(parsed.survey);
        if (contract !== undefined && contractHere !== contract) {
            return format === SURVEY_FILE_V2
                ? problemOf("Every language must ask the same questions: the same ids, types and order, and the same choice ids, selection " +
                    "limits and number ranges.", one.file, "variants")
                : problemOf("Choice ids, order and selection limits must match across languages.", one.file, "emotion");
        }
        contract = contractHere;
        claimedBy.set(language, one.file);
        survey[language] = parsed.survey;
        raw[language] = parsed.file;
    }
    const referenceSchema = format ?? SURVEY_FILE_V2;
    return {
        ok: true,
        questionnaire: { referenceSchema, survey },
        extraction: { referenceSchema, survey: raw },
    };
}
/** The questionnaire for one language, or `null`. There is no reference-locale fallback. */
export function surveyOf(questionnaire, language) {
    const found = questionnaire.survey[language];
    return found === undefined ? null : found;
}
/** The languages this questionnaire can actually be run in. */
export function surveyLanguageOf(questionnaire) {
    return Object.keys(questionnaire.survey);
}
/** Whether a reference schema code is a survey questionnaire's (either format). */
export function isSurveyReferenceSchema(code) {
    return code === SURVEY_FILE_V1 || code === SURVEY_FILE_V2;
}
/**
 * Narrows a stored `extraction` to a survey questionnaire, read into the model.
 *
 * This one re-runs the full schema over each language's script, because a survey agent's
 * whole output is the approved copy in that document: a missing `helpers.presence` is a call
 * where nobody says anything after four seconds of silence. Either format reads; the tag must
 * match the files (a v2 tag over v1 files is not a questionnaire).
 */
export function surveyExtractionOf(extraction) {
    if (typeof extraction !== "object" || extraction === null)
        return null;
    const shaped = extraction;
    if (typeof shaped.referenceSchema !== "string" || !isSurveyReferenceSchema(shaped.referenceSchema))
        return null;
    if (typeof shaped.survey !== "object" || shaped.survey === null)
        return null;
    const survey = {};
    for (const [language, one] of Object.entries(shaped.survey)) {
        if (isSurveyFileV2Shaped(one) !== (shaped.referenceSchema === SURVEY_FILE_V2))
            return null;
        if (shaped.referenceSchema === SURVEY_FILE_V2) {
            const parsed = SurveyFileV2Schema.safeParse(one);
            if (!parsed.success)
                return null;
            survey[language] = documentOfV2(parsed.data);
        }
        else {
            const parsed = SurveyDocumentSchema.safeParse(one);
            if (!parsed.success)
                return null;
            survey[language] = documentOfV1(parsed.data);
        }
    }
    return { referenceSchema: shaped.referenceSchema, survey };
}
//# sourceMappingURL=questionnaire.js.map